import { AuthState } from "../base/AuthState";
import template from "./PasswordResetTemplate.html";
import resultTemplate from "./PasswordResetResultTemplate.html";

import { _lcs } from "../../i18n";

/**
 * PasswordResetState — choose a new password (#/password-reset/{token}).
 *
 * Reached from TokenState for a resetPassword token. Redeeming the token
 * (PUT /auth/token/{token} with the new password) sets the password and signs
 * the account out everywhere, so the guest then logs in afresh.
 *
 * The token is looked up again on entry — the route can be opened directly
 * too. A link that is no use any more gets its own screen here rather than
 * TokenState's, whose messages are about account validation.
 */
export class PasswordResetState extends AuthState
{
    static ROUTE = "/password-reset/:token";
    static ID = "password-reset";

    // Same rule as the signup form (the core-api's floor is 6).
    static MIN_PASSWORD = 8;

    async enter()
    {
        this.saving = false;
        this.tokenHash = this.routeParams.getParam( "token" );

        const { token, status } = await this.loadToken();
        if ( !token )
        {
            return this._showResult( {
                title : _lcs( "Link ungültig" ),
                sub : _lcs( "Wir konnten deinen Link nicht bestätigen." ),
                icon : "triangle-alert",
                lead : 410 === status
                    ? _lcs( "Dieser Link ist abgelaufen. Bitte fordere einen neuen an." )
                    : _lcs( "Dieser Link wurde bereits verwendet oder ist nicht mehr gültig." ),
                retry : true
            } );
        }

        this.renderAuth( template, { email : token.actionData?.user?.email || "" } );

        const form = document.getElementById( "reset-form" );
        this.addEventListener( form, "submit", ( e ) => this.onSubmit( e ) );
        this.addEventListener( form, "click", ( e ) => this.onToggle( e ) );
        this.bind( "to-login", ( e ) =>
        {
            e.preventDefault();
            this.app.router.redirect( "/login" );
        } );
    }

    /** { token } if it is a live resetPassword token — else { token: null, status }. */
    async loadToken()
    {
        if ( !this.tokenHash )
        {
            return { token : null, status : 404 };
        }
        try
        {
            const { result } = await this.app.api.get( "/auth/token/" + encodeURIComponent( this.tokenHash ) );
            const token = ( result && +result.status === 200 ) ? result.data : null;
            return ( token && token.action === "resetPassword" ) ? { token } : { token : null, status : 404 };
        }
        catch ( err )
        {
            return { token : null, status : +err?.status };
        }
    }

    _showResult( data )
    {
        this.renderAuth( resultTemplate, data );
        this.bind( "to-login", () => this.app.router.redirect( "/login" ) );
        this.bind( "retry", () => this.app.router.redirect( "/password-forgotten" ) );
    }

    /** Show / hide one password field (the eye button inside it). */
    onToggle( e )
    {
        const toggle = e.target.closest( "[data-toggle]" );
        if ( !toggle )
        {
            return;
        }
        const input = document.getElementById( toggle.getAttribute( "data-toggle" ) );
        const show = input.type === "password";
        input.type = show ? "text" : "password";
        toggle.setAttribute( "aria-pressed", String( show ) );
        toggle.setAttribute( "aria-label", show ? _lcs( "Passwort verbergen" ) : _lcs( "Passwort anzeigen" ) );
        const icon = toggle.querySelector( ".icon" );
        icon.classList.toggle( "icon--eye", !show );
        icon.classList.toggle( "icon--eye-off", show );
        input.focus();
    }

    async onSubmit( e )
    {
        e.preventDefault();
        if ( this.saving )
        {
            return;
        }

        const errorEl = document.querySelector( '[data-id="reset-error"]' );
        const password = document.getElementById( "reset-new" )?.value || "";
        const repeat = document.getElementById( "reset-repeat" )?.value || "";
        errorEl.hidden = true;

        if ( password.length < PasswordResetState.MIN_PASSWORD )
        {
            return this._showError( errorEl, _lcs( "Das Passwort braucht mindestens 8 Zeichen." ) );
        }
        if ( password !== repeat )
        {
            return this._showError( errorEl, _lcs( "Die Passwörter stimmen nicht überein." ) );
        }

        const submitBtn = document.querySelector( '[data-id="save"]' );
        this._setLoading( submitBtn, true );
        try
        {
            const { result } = await this.app.api.put(
                "/auth/token/" + encodeURIComponent( this.tokenHash ),
                { password }
            );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected token response." );
            }

            this._showResult( {
                title : _lcs( "Passwort geändert" ),
                sub : _lcs( "Dein neues Passwort ist gespeichert." ),
                icon : "circle-check",
                lead : _lcs( "Aus Sicherheitsgründen wurdest du auf allen Geräten abgemeldet. Melde dich mit deinem neuen Passwort an." ),
                retry : false
            } );
        }
        catch ( err )
        {
            console.error( err );
            this._setLoading( submitBtn, false );
            this._showError( errorEl, this._errorMessageForStatus( +err?.status ) );
        }
    }

    _errorMessageForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Das Passwort braucht mindestens 8 Zeichen." );
            case 404: return _lcs( "Dieser Link wurde bereits verwendet oder ist nicht mehr gültig." );
            case 410: return _lcs( "Dieser Link ist abgelaufen. Bitte fordere einen neuen an." );
            default:  return _lcs( "Das Passwort konnte nicht geändert werden. Bitte versuche es später erneut." );
        }
    }

    bind( id, handler )
    {
        const el = document.querySelector( '[data-id="' + id + '"]' );
        if ( el ) this.addEventListener( el, "click", handler );
    }

    _setLoading( btn, on )
    {
        if ( !btn ) return;
        btn.disabled = on;
        btn.setAttribute( "aria-busy", String( on ) );
    }

    _showError( el, message )
    {
        if ( !el ) return;
        el.textContent = message;
        el.hidden = false;
    }

    async exit() {}
}
